import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    validateFirstname,
    ContactTypes,
    validateLastname,
    validateContactEmail,
    validateMobileNumber,
    validateSignatoryAfterCopy,
} from '../../../Validation';
import some from 'lodash/some';
import { SectionProps } from '../../types';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import Panel from "@sharedComponents/Panel";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import InputText from "@sharedComponents/InputText";

const SignatoryForm = (props: SectionProps) => {
    const {
        stepValues,
        setStepValues,
        onChangeValues,
        formErrors,
        setFormErrors,
    } = props
    const { t } = useTranslation()

    const onChangeFirstname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            signatory: {
                ...stepValues.contactInfo?.signatory,
                firstname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateFirstname(
            value,
            formErrors,
            ContactTypes.Signatory
        )
        setFormErrors(errors)
    }

    const onChangeLastname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            signatory: {
                ...stepValues.contactInfo?.signatory,
                lastname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateLastname(
            value,
            formErrors,
            ContactTypes.Signatory
        )
        setFormErrors(errors)
    }

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            signatory: {
                ...stepValues.contactInfo?.signatory,
                email: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateContactEmail(
            value,
            formErrors,
            ContactTypes.Signatory
        )
        setFormErrors(errors)
    }

    const onChangeMobile = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            signatory: {
                ...stepValues.contactInfo?.signatory,
                mobileNumber: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateMobileNumber(
            value,
            formErrors,
            ContactTypes.Signatory
        )
        setFormErrors(errors)
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            signatory: {
                ...stepValues.contactInfo?.signatory,
                phoneNumber: value,
            },
        }
        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })
    }

    const copyFromAgreement = () => {
        const fields = {
            firstname: stepValues.contactInfo?.agreement?.firstname,
            lastname: stepValues.contactInfo?.agreement?.lastname,
            email: stepValues.contactInfo?.agreement?.email,
            mobileNumber: stepValues.contactInfo?.agreement?.mobileNumber,
            phoneNumber: stepValues.contactInfo?.agreement?.phoneNumber,
        }

        const obj = {
            ...stepValues.contactInfo,
            signatory: fields,
        }
        setStepValues({ ...setStepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        validateSignatoryAfterCopy(fields, formErrors).then(
            (errors: FormErrorType[]) => {
                setFormErrors(errors)
            }
        )
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='producer-form producer-panel-wrapper'>
            <Panel className="panel--crb">
                <h3>{t('companyRegistration.signatory')}</h3>
                <Button
                    type={ButtonType.PRIMARY_ALT}
                    className="copy"
                    text={t('companyRegistration.copyFromAgreement')}
                    onClick={copyFromAgreement}
                />
                <div className='producer-form__fields'>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sig-firstname"
                            label={t('global.firstname')}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.Signatory}Firstname`,
                                ])
                            }
                            required={true}
                            value={
                                stepValues.contactInfo?.signatory?.firstname ||
                                ''
                            }
                            maxLength={50}
                            onChange={onChangeFirstname}
                            autoComplete="given-name"
                            errorText={uniqueErrors(`${ContactTypes.Signatory}Firstname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sig-lastname"
                            label={t('global.lastname')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.Signatory}Lastname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.signatory?.lastname ||
                                ''
                            }
                            maxLength={50}
                            onChange={onChangeLastname}
                            autoComplete="family-name"
                            errorText={uniqueErrors(`${ContactTypes.Signatory}Lastname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sig-email"
                            label={t('global.email')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.Signatory}Email`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.signatory?.email || ''
                            }
                            onChange={onChangeEmail}
                            type="email"
                            autoComplete="email"
                            maxLength={250}
                            errorText={uniqueErrors(`${ContactTypes.Signatory}Email`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sig-mobile"
                            label={t('global.mobileNumber')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.Signatory}MobileNumber`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.signatory
                                    ?.mobileNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangeMobile}
                            autoComplete="tel"
                            errorText={uniqueErrors(`${ContactTypes.Signatory}MobileNumber`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sig-phone"
                            label={t('global.phoneNumber')}
                            value={
                                stepValues.contactInfo?.signatory
                                    ?.phoneNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangePhone}
                            autoComplete="tel"
                        />
                    </div>
                </div>
            </Panel>
        </div>
    )
}

export default SignatoryForm;